import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { AppSettings } from 'pac-api';
import { Observable } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';

import { CART_NAME } from './cart-name';
import { CatchError } from './helpers';
import { LoggerService } from './logger.service';
import { CartModel } from './model/cart.model';
import { InvoiceDetailModel } from './model/invoice-detail.model';
import { PaymentInfo } from './model/payment-info.interface';
import { GenericErrorHandlerService } from './shared/generic-error-handler.service';

@Injectable({
  providedIn: 'root'
})
export class CartService {

  public Cart: CartModel;

  public Invoice: CartModel;

  public Split: CartModel;

  private SplitCartName: string;

  private inflight: {[key: string]: Observable<CartModel>} = {};

  public get Data(): CartModel {
    return this.Invoice ? this.Invoice : this.Cart;
  }

  public get Association(): string {
    return this.settings.pac_base_asn_code;
  }

  public Show: Set<'None' | 'Subtotal' | 'Discounts' | 'Total'> = new Set();

  constructor(
    private http: HttpClient,
    private settings: AppSettings,
    private genericErrorHandler: GenericErrorHandlerService,
    private logger: LoggerService,
    @Inject(CART_NAME) private defaultName: string,
  ) {
    let invoice = sessionStorage.getItem('CartService.Invoice');
    let cart = sessionStorage.getItem('CartService.Cart');
    let split = sessionStorage.getItem('CartService.Split');

    this.SplitCartName = sessionStorage.getItem('CartService.SplitCartName');

    if (invoice) {
      invoice = JSON.parse(invoice);
      this.Invoice = new CartModel(invoice);
    } else if (cart) {
      cart = JSON.parse(cart);
      this.Cart = new CartModel(cart);

      // Behind the scene, lets refresh from the server
      this.Get().subscribe();

      if (split) {
        split = JSON.parse(split);
        this.Split = new CartModel(split);
      }

      if (this.SplitCartName) {
        this.Get(false, this.SplitCartName).subscribe();
      }
    }
  }

  public Cache(data: CartModel, type: 'Invoice' | 'Cart' | 'Split' = 'Cart') {
    this[type] = data;
    sessionStorage.setItem('CartService.' + type, JSON.stringify(data));

    if (type === 'Cart') {
      sessionStorage.removeItem('CartService.Invoice');
    } else if (type === 'Invoice') {
      sessionStorage.removeItem('CartService.Cart');
    }
  }

  public Clear(): void {
    sessionStorage.removeItem('CartService.Cart');
    sessionStorage.removeItem('CartService.Invoice');
    sessionStorage.removeItem('CartService.Split');
  }

  public New(): Observable<CartModel> {
    return this.Get(true);
  }

  public Get(createNew: boolean = false, cart: string = this.defaultName): Observable<CartModel> {
    this.Clear();

    if (this.inflight[cart]) {
      return this.inflight[cart];
    }

    this.inflight[cart] = this.http.get(
      this.settings.pac_base_nf_url + 'api/Cart/' + (createNew ? 'New' : 'Get') + (cart ? '?cart=' + cart : ''),
      {
        withCredentials: true
      }
    ).pipe(
      tap({
        complete: () => {
          this.inflight[cart] = null;
        },
      }),
      catchError(CatchError),
      map((result: any) => {
        if (result.error) {
          return result;
        } else if ('inv_key' in result) {
          return new CartModel(result);
        }
      }),
      shareReplay(1),
      tap({
        next: result => {
          try {
            this.logger.info(result);
            this.Invoice = null;
            this.Cache(result, cart ? 'Split' : 'Cart');
          } catch { }
        },
        error: this.genericErrorHandler.Hanlder
      })
    );

    return this.inflight[cart];
  }

  public PackageCombo(p02: string, cart: string = this.defaultName): Observable<CartModel> {
    const observable = this.http.post(
      this.settings.pac_base_nf_url + 'api/Cart/PackageCombo/' + p02 + (cart ? '?cart=' + cart : ''),
      null,
      {
        withCredentials: true
      }
    ).pipe(catchError(CatchError), map((result: any) => {
      if (result.error) {
        return result;
      } else if ('inv_key' in result) {
        return new CartModel(result);
      }
    }), tap(result => {
      try {
        this.logger.info(result);
        this.Cache(result);
      } catch { }
    }, this.genericErrorHandler.Hanlder));

    return observable;
  }

  public ApplyMemberDiscount(promo?: string, affinity?: number): Observable<CartModel> {
    let url = this.settings.pac_base_nf_url + 'api/Cart/ApplyMemberDiscount';

    if (promo) {
      url += '?promo=' + promo;
    }

    if (affinity) {
      url += (promo) ? '&' : '?';
      url += 'affinity=' + affinity;
    }

    const observable = this.http.post(
      url,
      null,
      {
        withCredentials: true
      }
    ).pipe(catchError(CatchError), map((result: any) => {
      if (result.error) {
        return result;
      } else if ('inv_key' in result) {
        return new CartModel(result);
      }
    }), tap(result => {
      try {
        this.logger.info(result);
      } catch {
      }

      this.Cache(result);
    }, this.genericErrorHandler.Hanlder));
    return observable;
  }

  public AddExamCoachSubscription(prc_code: string = null, cart: string = this.defaultName): Observable<CartModel> {

    let param = '';

    if (prc_code) {
      param += 'prc_code=' + encodeURIComponent(prc_code);
    }

    if (cart) {
      if (param) {
        param += '&';
      }
      param += 'cart=' + cart;
    }

    const observable = this.http.post(
      this.settings.pac_base_nf_url + 'api/Cart/AddExamCoachSubscription' + (param ? '?' + param : ''),
      {},
      {
        withCredentials: true
      }
    ).pipe(catchError(CatchError), map((result: any) => {
      if (result == null) {
        throw new Error('Shopping cart is empty');
      }

      if (result.error) {
        return result;
      } else if ('inv_key' in result) {
        return new CartModel(result);
      }
    }), tap(result => {
      try {
        this.logger.info(result);
        this.Cache(result);
      } catch { }

    }, this.genericErrorHandler.Hanlder));

    return observable;
  }

  public Add5mmSubscriptionPlus(): Observable<CartModel> {
    const trm_start_date = new Date();
    const trm_end_date = new Date(trm_start_date);
    trm_end_date.setFullYear(trm_start_date.getFullYear() + 1);

    return this.Add5mmSubscription('dbd34d63-53be-49d4-b8e3-990b82014b9e', {
      trm_start_date: trm_start_date.toDateString(),
      trm_end_date: trm_end_date.toDateString(),
    });
  }

  public Add5mmSubscription12(): Observable<CartModel> {
    const trm_start_date = new Date();
    const trm_end_date = new Date(trm_start_date);
    trm_end_date.setFullYear(trm_start_date.getFullYear() + 1);

    return this.Add5mmSubscription('6947bdd5-0976-4dd3-98f0-031bcaadcec4', {
      trm_start_date: trm_start_date.toDateString(),
      trm_end_date: trm_end_date.toDateString(),
    });
  }

  private Add5mmSubscription(prc_key: string, param: any, cart: string = this.defaultName): Observable<CartModel> {
    return this.http.put(
      this.settings.pac_base_nf_url + 'api/Cart/Subscription/' + prc_key + (cart ? '?cart=' + cart : ''), param,
      {
        withCredentials: true
      }
    ).pipe(catchError(CatchError), map((result: any) => {
      if (result.error) {
        return result;
      } else if ('inv_key' in result) {
        return new CartModel(result);
      }
    }), tap(result => {
      try {
        this.logger.info(result);
        this.Cache(result);
      } catch { }

    }, this.genericErrorHandler.Hanlder));
  }

  public AddBPPSubscription(price: number): Observable<CartModel> {
    const observable = this.http.post(
      this.settings.pac_base_nf_url + 'api/Cart/AddBPPSubscription',
      price,
      {
        withCredentials: true
      }
    ).pipe(catchError(CatchError), map((result: any) => {
      if (result.error) {
        return result;
      } else if ('inv_key' in result) {
        return new CartModel(result);
      }
    }), tap(result => {
      try {
        this.logger.info(result);
        this.Cache(result);
      } catch { }
    }, this.genericErrorHandler.Hanlder));

    return observable;
  }

  public AddMerchandise(key: string):Observable<InvoiceDetailModel> {
    const observable = this.http.post(
      this.settings.pac_base_nf_url + 'api/Cart/InvoiceDetails/' + key,
      {},
      {
        withCredentials: true
      }
    ).pipe(catchError(CatchError), map((result: any) => {
      if (result.error) {
        return result;
      } else if ('ivd_key' in result) {
        return new InvoiceDetailModel(result);
      }
    }), tap(result => {
      try {
        this.logger.info(result);
        this.Cache(result);
      } catch { }
    }, this.genericErrorHandler.Hanlder));

    return observable;
  }

  public UpdatePayment(
    payment: PaymentInfo,
    type?: 'prepaid' | 'installment payments' | 'terms',
    autoPay?: boolean,
    cart?: string
  ): Observable<CartModel> {
    let url = this.settings.pac_base_nf_url + 'api/Cart/UpdatePayment';

    if (type) {
      url += '?type=' + type;
    }

    if (autoPay) {
      url += (type) ? '&' : '?';
      url += 'mbr_auto_pay=' + (autoPay ? '1' : '0');
    }

    if (cart) {
      url += (type || autoPay) ? '&' : '?';
      url += 'cart=' + cart;
    }

    const observable = this.http.put(
      url,
      payment ? {
        pin_cpi_key: payment.cpi_key,
        pin_apm_key: payment.cpi_apm_key
      } : null,
      {
        withCredentials: true
      }
    ).pipe(catchError(CatchError), map((result: any) => {
      if (result.error) {
        return result;
      } else if ('inv_key' in result) {
        return new CartModel(result);
      }
    }), tap(result => {
      try {
        this.logger.info(result);
        this.Cache(result, cart ? 'Split' : 'Cart');
      } catch { }
    }, this.genericErrorHandler.Hanlder));

    return observable;
  }

  public Checkout(extra?: any, cart?: string): Observable<CartModel> {
    if (!extra) {
      extra = {};
    }

    const observable = this.http.post(
      this.settings.pac_base_nf_url + 'api/Cart/Checkout' + (cart ? '?cart=' + cart : ''),
      extra,
      {
        withCredentials: true
      }
    ).pipe(catchError(CatchError), map((result: any) => {
      if (result.error) {
        return result;
      } else if ('inv_key' in result) {
        return new CartModel(result);
      }
    }), tap(result => {
      try {
        if (! cart) {
          this.Clear();
        }

        this.logger.info(result);
        this.Cart = null;
        this.Cache(result, cart ? 'Split' : 'Invoice');

        if (cart) {
          sessionStorage.removeItem('CartService.SplitCartName');
        }
      } catch { }
    }, this.genericErrorHandler.Hanlder));

    return observable;
  }

  public SplitCart(details: InvoiceDetailModel[], cart: string): Observable<CartModel[]> {
    const observable = this.http.post(
      this.settings.pac_base_nf_url + 'api/Cart/Split?cart=' + cart,
      details.map(ivd => ivd.ivd_key),
      {
        withCredentials: true
      }
    ).pipe(
      catchError(CatchError),
      map((result: any) => {
        if (result.error) {
          throw result;
        } else if (result instanceof Array) {
          return result.map(inv => new CartModel(inv));
        }
      }),
      tap(([cart1, cart2]) => {
        try {
          this.logger.info(cart1, cart2);
          this.Cache(cart1, 'Cart');
          this.Cache(cart2, 'Split');

          this.SplitCartName = cart;
          sessionStorage.setItem('CartService.SplitCartName', cart);
        } catch {
        }
      }, this.genericErrorHandler.Hanlder)
    );

    return observable;
  }

  public GetMonthlyInstallmentsEligible(): boolean {
    if (!this.Cart || !this.Cart.Memberships) {
      return false;
    }

    const abmpCertified = this.Cart.Memberships.find(mbr =>
      mbr.mbt_flip_customer_member_flag
      && mbr.mbt_includes_insurance_flag_ext
      && mbr.mbr_asn_code === 'ABMP'
      && mbr.mbt_code === 'Certified'
    );

    if (abmpCertified) {
      return true;
    }

    const cosmoProf = this.Cart.Memberships.find(mbr =>
      mbr.mbt_flip_customer_member_flag
      && mbr.mbt_includes_insurance_flag_ext
      && (mbr.mbr_asn_code === 'ASCP' || mbr.mbr_asn_code === 'ANP')
      && mbr.mbt_code === 'Professional'
    );
    const ahp = this.Cart.Memberships.find(mbr =>
      mbr.mbt_flip_customer_member_flag
      && mbr.mbt_includes_insurance_flag_ext
      && (mbr.mbr_asn_code === 'AHP')
      && mbr.mbt_code === 'Hair Professional'
    );
    if(cosmoProf || ahp) {
      return true;
    }

    if (this.Cart.IsECPlus) {
      return true;
    }

    return false;
  }

  public Subtotals(): Array<{ item: string, type: string, price: number }> {
    let results = [];

    if (this.Data) {
      results = this.Data.InvoiceDetails.map(ivd => {
        let prc_display_name: string;

        if (ivd.ptp_code === 'Membership Dues') {
          const membership = this.Data.Memberships.find(mbr => mbr.mbr_key === ivd.ivd_mbr_key);
          prc_display_name = membership.mbr_asn_code + ' ' + membership.mbt_code;
        } else {
          prc_display_name = ivd.prc_display_name;
        }

        return {
          item: prc_display_name,
          price: ivd.ivd_price,
          type: ivd.ptp_code
        };
      });
    }

    return results;
  }

  public get Dues(): number {
    return this.Subtotals()
      .filter(item => item.type === 'Membership Dues')
      .reduce((previous, item) => previous + (isNaN(item.price) ? 0 : item.price), 0);
  }

  public get Subscriptions(): number {
    return this.Subtotals()
      .filter(item => item.type === 'Subscription')
      .reduce((previous, item) => previous + (isNaN(item.price) ? 0 : item.price), 0);
  }

  public get Other(): number {
    return this.Subtotals()
      .filter(item => item.type !== 'Subscription' &&  item.type !== 'Membership Dues')
      .reduce((previous, item) => previous + (isNaN(item.price) ? 0 : item.price), 0);
  }

  public get Subtotal(): number {
    return this.Subtotals().reduce((previous, item) => previous + (isNaN(item.price) ? 0 : item.price), 0);
  }

  public Discounts(): Array<{ item: string, price: number }> {
    let results = [];

    if (this.Data) {
      results = this.Data.AdditionalInvoiceDetails.filter(ivd => ivd.ptp_code === 'Discount').map(ivd => {
        return {
          item: ivd.prc_display_name,
          price: ivd.ivd_price
        };
      });
    }

    return results;
  }

  public get Discount(): number {
    return this.Discounts().reduce((previous, item) => previous + (isNaN(item.price) ? 0 : item.price), 0);
  }

  public get Credits(): number {
    return this.Data ? this.Data.inv_netcredit : 0;
  }

  public get Total(): number {
    if (!this.Data) {
      return 0;
    }

    return this.Data.Total;
  }

  public get isECPlusMonthly(): boolean {
    if (!this.Split) {
      return false;
    }

    const ec = this.Data.InvoiceDetails.some(ivd => ivd.prd_code === 'AExamCoach');
    const a5mm = this.Split.InvoiceDetails.some(ivd => ivd.prd_code === 'A5mm');

    return ec && a5mm;
  }
}
